/**
 * Account Detail Page
 * 
 * Consolidated view of an Account (Advertiser + Client context) with tabs for:
 * - Overview (brand info)
 * - Bookings
 * - Campaigns
 * - Contacts (from linked Client)
 * - Billing (from linked Client)
 */

import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Loader2,
    ArrowLeft,
    Building2,
    FileText,
    Briefcase,
    CreditCard,
    Users
} from "lucide-react";
import { useLocation } from "wouter";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { apiJson } from "@/lib/api";
import * as accountsApi from "@/api/accounts";
import type { Account } from "@/api/accounts";
import { ContactsTab } from "@/pages/admin-clients/contacts-tab";
import { ClientService } from "@/services/ClientService";

export default function AccountDetailPage() {
    const [, params] = useRoute("/accounts/:id");
    const [, setLocation] = useLocation();
    const id = params?.id ? parseInt(params.id) : 0;

    // Fetch account details with full client enrichment
    const { data: account, isLoading } = useQuery<Account>({
        queryKey: ["account", id],
        queryFn: () => accountsApi.fetchAccount(id),
        enabled: !!id,
    });

    // Fetch bookings for this account (via advertiserId)
    const { data: bookingsResponse, isLoading: bookingsLoading } = useQuery<{ success: boolean; data: { bookings: any[] } }>({
        queryKey: ["bookings", "account", id],
        queryFn: () => apiJson(`/api/bookings?advertiserId=${id}`),
        enabled: !!id,
    });
    const bookings = bookingsResponse?.data?.bookings || [];

    // Fetch campaigns for this account (via advertiserId)
    const { data: campaignsData, isLoading: campaignsLoading } = useQuery<{ data: any[] }>({
        queryKey: ["campaigns", "account", id],
        queryFn: () => apiJson(`/api/campaigns?advertiserId=${id}&pageSize=100`),
        enabled: !!id,
    });
    const campaigns = campaignsData?.data || [];

    // Fetch contacts for this account's client
    const { data: contactsResponse } = useQuery({
        queryKey: ["client-contacts", account?.clientId],
        queryFn: () => ClientService.getContacts(account!.clientId!),
        enabled: !!account?.clientId,
    });
    const contacts = contactsResponse?.data || [];

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!account) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-xl font-semibold">Account not found</h2>
                    <Button variant="link" onClick={() => setLocation("/accounts")}>
                        Back to Accounts
                    </Button>
                </div>
            </div>
        );
    }

    const hasBilling = account.clientId && account.billing;
    const hasClientContact = account.clientId && account.clientContact;

    return (
        <div className="p-6 space-y-6">
            <Button
                variant="ghost"
                className="mb-2 pl-0 hover:pl-2 transition-all"
                onClick={() => setLocation("/accounts")}
            >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Accounts
            </Button>

            <PageHeader
                title={account.name}
                description={
                    <div className="flex items-center gap-2 mt-1">
                        <Badge variant={account.isActive ? "default" : "secondary"}>
                            {account.isActive ? "Active" : "Inactive"}
                        </Badge>
                        {account.clientName && (
                            <>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground text-sm">
                                    Billing: {account.clientName}
                                </span>
                            </>
                        )}
                    </div>
                }
            />

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="bookings">
                        Bookings
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {bookings.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="campaigns">
                        Campaigns
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {campaigns.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="contacts">
                        Contacts
                        {account?.clientId && (
                            <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                                {contacts.length}
                            </Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="billing">Billing</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Address</CardTitle>
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-sm mt-2 whitespace-pre-wrap">
                                {account.address || "Not recorded"}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Bookings Tab */}
                <TabsContent value="bookings">
                    <Card>
                        <CardHeader>
                            <CardTitle>Account Bookings</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {bookingsLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : bookings.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Booking Name</TableHead>
                                            <TableHead>Cycle</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Value</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {bookings.map((booking: any) => (
                                            <TableRow key={booking.id}>
                                                <TableCell className="font-medium">
                                                    <a
                                                        href={`/admin/bookings/${booking.id}`}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {booking.name || `Booking #${booking.id}`}
                                                    </a>
                                                </TableCell>
                                                <TableCell>
                                                    {booking.cycleName || (booking.cycleId ? `Cycle ${booking.cycleId}` : "—")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={booking.status === "confirmed" ? "default" : "secondary"}>
                                                        {booking.status || "Draft"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {booking.bookingValue ? `€${Number(booking.bookingValue).toLocaleString()}` : "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <Briefcase className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No bookings for this account yet.</p>
                                    <p className="text-sm mt-1">
                                        <a
                                            className="text-blue-600 hover:underline cursor-pointer"
                                            onClick={() => setLocation('/admin/bookings')}
                                        >
                                            Create a booking
                                        </a>
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Campaigns Tab */}
                <TabsContent value="campaigns">
                    <Card>
                        <CardHeader>
                            <CardTitle>Account Campaigns</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {campaignsLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : campaigns.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Campaign Name</TableHead>
                                            <TableHead>Account</TableHead>
                                            <TableHead>Cycle</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Booking</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {campaigns.map((campaign: any) => (
                                            <TableRow key={campaign.id}>
                                                <TableCell className="font-medium">
                                                    <a
                                                        href={`/campaigns/${campaign.id}`}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {campaign.name}
                                                    </a>
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.advertiserName || "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.cycleName || (campaign.cycleId ? `Cycle ${campaign.cycleId}` : "—")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={campaign.status === "active" ? "default" : "secondary"}>
                                                        {campaign.status || "Draft"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.bookingId ? (
                                                        <a
                                                            href={`/admin/bookings/${campaign.bookingId}`}
                                                            className="text-blue-600 hover:text-blue-800 hover:underline"
                                                        >
                                                            {campaign.bookingName || `#${campaign.bookingId}`}
                                                        </a>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                                                            Unassigned
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <FileText className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No campaigns for this account yet.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Contacts Tab */}
                <TabsContent value="contacts">
                    {account.clientId ? (
                        <ContactsTab clientId={account.clientId} />
                    ) : (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="h-5 w-5" />
                                    Contacts
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <Users className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No billing entity linked to this account.</p>
                                    <p className="text-sm mt-1">
                                        Link a billing entity to manage contacts.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Billing Tab */}
                <TabsContent value="billing">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="h-5 w-5" />
                                Billing Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {hasBilling ? (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-4">
                                        <div>
                                            <span className="font-semibold block text-xs text-muted-foreground">
                                                Legal / Billing Name
                                            </span>
                                            <span className="text-sm">
                                                {account.billing?.billingName || account.clientName || "Same as account name"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="font-semibold block text-xs text-muted-foreground">
                                                VAT Number
                                            </span>
                                            <span className="text-sm">
                                                {account.billing?.vatNumber || "Not recorded"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="font-semibold block text-xs text-muted-foreground">
                                                Payment Terms
                                            </span>
                                            <span className="text-sm">
                                                {account.billing?.paymentTerms || "Standard"}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">
                                            Billing Address
                                        </span>
                                        <div className="text-sm whitespace-pre-wrap mt-1">
                                            {account.billing?.billingAddress || "Not recorded"}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <CreditCard className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No billing entity linked to this account.</p>
                                    <p className="text-sm mt-1">
                                        Edit this account to link a billing entity, or{' '}
                                        <a
                                            className="text-blue-600 hover:underline cursor-pointer"
                                            onClick={() => setLocation('/admin/clients')}
                                        >
                                            create one first
                                        </a>.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
